'use client'

/* ---------------------------------
Timeline
--------------------------------- */

import React, { ReactElement } from 'react'
import {
  Anchor,
  HighlightCard,
  Image,
  OpeningTitle,
  Paragraph,
  Section,
  TextCard,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import Link from 'next/link'
import NextImage from 'next/image'
import {
  HighlightCardWithPatchedOnClick,
  TextCardWithPatchedOnClick,
  TimelineModel,
} from '@/models/Timeline'
import DOMPurify from 'isomorphic-dompurify'
import { SectionBackground } from '@/types'
import { handleOnClickModel } from '@/helpers/models'

type OwnProps = {
  props: ReturnType<typeof TimelineModel>['props']
  modelledContent: ReturnType<typeof TimelineModel>['modelledContent']
}

export default function Timeline({
  props,
  modelledContent: extractedContent,
}: OwnProps): ReactElement {
  const {
    openingTitle,
    title: _title,
    paragraph: _paragraph,
    cta,
    highlightCard,
    textCards,
    image,
  } = extractedContent ?? {}

  const { direction, distribution, bg } = props ?? {}
  const { fit, imageProps } = image
  const { content: titleContent, ...title } = _title
  const { content: paragraphContent, ...paragraph } = _paragraph
  const highlightCardOnClickData = highlightCard?.onClick

  return (
    <Section
      bg={bg as SectionBackground | undefined}
      className={`section-timeline`}
      data-direction={direction ?? 'row'}
      data-distribution={distribution ?? 'text-image'}
    >
      <div className='section-timeline__grid'>
        {/* COL 1 */}
        <div className={`section-timeline__grid__col-1`}>
          <div className={`section-timeline__title-wrapper `}>
            {openingTitle.children ? (
              <OpeningTitle
                className={`section-timeline__opening-title`}
                {...openingTitle}
              />
            ) : null}

            {titleContent ? (
              <Title
                className={`section-timeline__title`}
                {...title}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(titleContent),
                }}
              />
            ) : null}
          </div>

          {paragraphContent ? (
            <Paragraph
              // We override Paragraph's default alignment by following the Title's
              // alignment; this is needed because the WYSIWYG editor won't pass
              // any style for its default alignment (which is left).
              // If the user sets an alignment other than left in the WYSIWYG editor,
              // the resulting HTML with inline styles will override this alignment.
              alignment={title?.alignment}
              as={'div'}
              className={`section-timeline__paragraph`}
              // TODO sanitize?
              dangerouslySetInnerHTML={{ __html: paragraphContent }}
              {...paragraph}
            />
          ) : null}

          {(highlightCard as HighlightCardWithPatchedOnClick | null) ? (
            <HighlightCard
              className={'section-timeline__highlight-card'}
              data-clickable={!!highlightCardOnClickData}
              {...highlightCard}
              {...(highlightCardOnClickData
                ? // We have to rewrite the onClick, if present
                  { onClick: handleOnClickModel(highlightCardOnClickData) }
                : {})}
            />
          ) : null}

          {cta?.label && cta?.href ? (
            <Anchor
              wrapper={'div'}
              data-alignment={cta.alignment}
              className={`section-timeline__cta link-button-themed`}
              as={Link}
              {...cta}
            >
              {cta.label}
            </Anchor>
          ) : null}
        </div>

        {/* COL 2 */}
        <div className='section-timeline__grid__col-2'>
          {(textCards?.length as Array<TextCardWithPatchedOnClick> | null)
            ? textCards.map(
                (textCard: TextCardWithPatchedOnClick, i: number) => {
                  const { onClick, ...rest } = textCard

                  return (
                    <TextCard
                      key={i + '__' + textCard.cardTitle}
                      className='section-timeline__text-card'
                      clickable={!!onClick}
                      {...rest}
                      {...(onClick
                        ? { onClick: handleOnClickModel(onClick) }
                        : {})}
                    />
                  )
                }
              )
            : null}

          {imageProps?.src ? (
            // `alt` already included in props spread
            // eslint-disable-next-line jsx-a11y/alt-text
            <Image
              fit={fit}
              className={`section-timeline__image`}
              as={NextImage}
              imageProps={{
                ...imageProps,
                // If not present (which it will not be,
                // since the CMS is not returning them),
                // it'll take the default value.
                // TODO find out why these props were expected here,
                // @ts-ignore
                width: imageProps.width || 400,
                // @ts-ignore
                height: imageProps.height || 400,
              }}
            />
          ) : null}
        </div>
      </div>
    </Section>
  )
}
